<template>
  <v-card elevation="0" id="batch-copy">
    <!-- Slideout Header -->
    <v-list class="branded-background">
      <v-list-tile avatar>
        <v-list-tile-avatar>
          <v-icon color="white">file_copy</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content class="modal-title">
          <span class="text-white">Batch Copy</span>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
      <!-- Bulb Form -->
    <v-card id="batch-copy-form" elevation="0" v-if="copies" >
      <div v-for="bulb in _.orderBy(bulbs, 'id')" :key="bulb.id">
        <!-- Bulb Titles -->
        <v-list :three-line="bulb.tags.length > 0">
          <v-list-tile>
            <!-- Regular -->
            <template  v-if="!bulb.lmb_id">
              <v-list-tile-content class="text-truncate" style="height: auto">
               <v-list-tile-title>{{bulb.name}}</v-list-tile-title>
                <v-list-tile-sub-title class="pl-4" >
                  <v-chip v-for="tag in bulb.tags" :key="tag.id" outline :style="tagStyle(tag)" >{{tag.text}}</v-chip>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
            <!-- LMB -->
            <template v-if="bulb.lmb_id > 0">
              <v-list-tile-content>
                {{bulb.name}} is a Learner Made Bulb and cannot be copied.
              </v-list-tile-content>
            </template>
          </v-list-tile>
          <v-spacer></v-spacer>
        </v-list>  
        <template v-if="!bulb.lmb_id">
          <!-- Name editor for new copies -->
          <CopyRow v-for="(copy,copyIndex) in copies[bulb.id]" :batchCopying="copyStatus !== 'not_started' " :copies="copies[bulb.id]" :copy="copy" :copy-index="copyIndex" :key="copy.key"></CopyRow>
          <!--Create more copies-->
          <v-btn v-if="copies[bulb.id].length < 10 && copyStatus === 'not_started'" color="primary" flat text  @click="addCopy(bulb)" class="pl-4"><v-icon class="pr-1">add</v-icon> Additional Copy</v-btn>
        </template>
      </div>
    </v-card>
    <!-- Bottom of Slideout Buttons -->
    <v-card-actions>
      <v-btn text @click="closeBatchCopy" :disabled="copyStatus === 'copying'" flat>Close</v-btn>
      <v-spacer></v-spacer>
      <v-btn v-if="copyStatus === 'not_started' "  @click="copyBatch" class="branded-background text-white" flat text>
        Copy
      </v-btn>
      <v-btn v-if="copyStatus !== 'not_started'"  @click="closeBatchCopy" :disabled="copyStatus === 'copying'" flat :loading="copyStatus === 'copying' ">
        <v-icon color="success">done</v-icon>
        Copying is Complete
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
/* global _ */
/* global moment */
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import _ from 'lodash';
const Color = require('color');

import CopyRow from './CopyRow';

export default {
  name: 'BatchCopy',
  components: { 
    CopyRow,
  },
  props: [ 
    'bulbs'
  ], 
  data() {
    return {
      copies: null,
      copyStatus: 'not_started', 
    }
  },
  methods: {
    ...mapMutations([
      'setDashModal',
      'slideOut',
      'triggerSnackbar'
    ]),
    ...mapActions('instructors', [
      'copyBulb'
    ]),
    addCopy(bulb) {
      if (this.copies[bulb.id].length < 10) {
        this.copies[bulb.id].push({
          name: `Copy ${this.copies[bulb.id].length + 1} of ${bulb.name}`,
          copying: false, 
          editing: false, 
          errors: [],
          key: `copy_${bulb.id}_${new Date().getTime()}`,
          success: false,
        });
      } else {
        // toast: too many copies
        this.triggerSnackbar( { text: "Cannot make more than 10 copies of a single bulb", color: 'error', timeout: 6000 })
      }
    },
    closeBatchCopy() {
      // only refresh if there was action to copy
      if (this.copyStatus !== 'not_started') this.$store.commit('instructors/refreshData');
      this.slideOut({toggle: false, view: null});
    },
    async copyBatch() {
      // trigger sequential copy
      this.copyStatus = 'copying';
      for (const bulbId of Object.keys(this.copies)) {
        for (const copy of this.copies[bulbId]) {
          if (copy.errors.length === 0) {
            copy.copying = true;
            copy.success = false;
            const res = await this.copyBulb({
              id: bulbId,
              name: copy.name,
              edit:false, 
              newDb:true,
              lti:false,
              fullCopy: true,
              refreshData: false, // prevent reload after every copy
            });
            if (res instanceof Error || res.message ) {
              copy.errors.push(`${res.name || 'Copy Error' } - ${res.message || 'unknown' }`);
            } else if (!res) {
              copy.errors.push('Other operation in progress.');
            } else if (res.id) {
              copy.success = true;
            }
            copy.copying = false;
            await this.$nextTick();
          } 
        }
      }
      this.copyStatus = 'complete';
      this.triggerSnackbar({ text:"Batch Copy Complete", color: 'Success', timeout: 6000 });
    },
    tagStyle(data) {
      let color = (data.color) ? data.color : '#FAFAFA';
      let darkColor = Color(color).darken(0.60).hex();
      return {
        'backgroundColor': `${color} !important`,
        'color': `${darkColor} !important`,
        'border': `solid 1px ${darkColor} !important`,
        'borderRadius': '20px',
        'cursor': 'pointer'
      }
    },
  },
  async mounted() {
    this.copies = {};
    _.map(this.bulbs, (b, idx) => {
      // create empty array of copies
      const copyTemplate = [];
      // add first copy as long as the bulb isn't lmb
      if (!b.lmb_id) {
        copyTemplate.push({
          name: `Copy of ${b.name}`,
          copying: false, 
          editing: false, 
          errors: [],
          key: `copy_${b.id}_0`,
          success: false, 
        })
      }
      // attach to the copies object so vue can track updates
      this.$set(this.copies, b.id, copyTemplate)
    });
    window.addEventListener('beforeunload', async (event) => {
      if (this.copyStatus === 'copying') {
        event.preventDefault();
        event.returnValue = 'Bulbs are still copying.';
      }
 		});
  },
};
</script>

<style>
  #batch-copy {
    height: 100vh;
    overflow-y: hidden;
  }
  #batch-copy-form {
    height: calc(100vh - 110px);
    overflow-y: auto;
  }
  #batch-copy .v-list--three-line .v-list__tile {
    height: auto !important;
  }
</style>